import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import classList from 'flarum/common/utils/classList';

/** Media types the picker can browse. Must match KlipyClient's constants. */
const TABS = ['gifs', 'stickers'];

/**
 * How close to the bottom of the scroll area we start loading the next page.
 * Roughly one row, so the grid keeps growing before the user hits the end.
 */
const INFINITE_SCROLL_MARGIN = 300;

/**
 * Column counts per tab, mirroring the LESS. They have to agree because the
 * columns are filled here in JS rather than by CSS `column-count`: the CSS
 * version reflows every tile when a new page is appended, so whatever the user
 * was looking at jumps away mid-scroll.
 */
const COLUMNS = { gifs: 3, stickers: 4 };
const COLUMNS_PHONE = { gifs: 2, stickers: 3 };

const PHONE_MAX_WIDTH = 767;

/**
 * Distributes items into columns, always adding the next item to the shortest
 * column so the columns end up roughly level.
 *
 * Heights are measured in aspect ratio units (height / width), which is what
 * the tiles actually occupy once they are stretched to the column width.
 */
function buildColumns(items, columnCount) {
  const columns = [];
  const heights = [];

  for (let i = 0; i < columnCount; i++) {
    columns.push([]);
    heights.push(0);
  }

  items.forEach((item) => {
    let shortest = 0;

    for (let i = 1; i < columnCount; i++) {
      if (heights[i] < heights[shortest]) shortest = i;
    }

    columns[shortest].push(item);
    heights[shortest] += item.width > 0 && item.height > 0 ? item.height / item.width : 1;
  });

  return columns;
}

/**
 * The GIF and sticker picker.
 *
 * Talks only to our own backend endpoint, never to KLIPY directly, so the API
 * key stays on the server.
 */
export default class GifPickerModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);

    this.type = TABS[0];
    this.query = '';

    // Each tab keeps its own results, so switching back does not throw away a
    // scrolled-through grid or refetch what we already have.
    this.state = {};
    TABS.forEach((type) => {
      this.state[type] = { items: [], page: 1, hasNext: false, loading: true, error: null };
    });

    this.loadingMore = false;
    this.searchTimeout = null;

    // Crossing the phone breakpoint changes the column count, and the columns
    // are built here rather than by CSS, so a resize has to redraw.
    this.columns = this.columnCount();
    this.onResize = () => {
      const columns = this.columnCount();

      if (columns === this.columns) return;

      this.columns = columns;
      m.redraw();
    };

    window.addEventListener('resize', this.onResize);

    this.load();
  }

  onremove(vnode) {
    super.onremove(vnode);

    if (this.searchTimeout) clearTimeout(this.searchTimeout);

    window.removeEventListener('resize', this.onResize);
  }

  className() {
    return 'HappyGify-modal Modal--large';
  }

  title() {
    return app.translator.trans('happydigital-gify.forum.modal_title');
  }

  /** The state record for the tab currently on screen. */
  current() {
    return this.state[this.type];
  }

  /**
   * Matched to the breakpoint the LESS uses, so the JS-built columns and the
   * stylesheet never disagree about how many there are.
   */
  columnCount() {
    const phone = typeof window !== 'undefined' && window.innerWidth <= PHONE_MAX_WIDTH;

    return (phone ? COLUMNS_PHONE : COLUMNS)[this.type];
  }

  /**
   * Fetches a page. `append` keeps the existing grid for infinite scrolling.
   */
  load(append = false) {
    // Captured now: by the time the response lands the user may have typed
    // something else or switched tabs, and a stale result must not overwrite a
    // newer one.
    const type = this.type;
    const requestedQuery = this.query;
    const state = this.state[type];

    if (append && (this.loadingMore || !state.hasNext)) return;

    const page = append ? state.page + 1 : 1;

    if (append) {
      this.loadingMore = true;
    } else {
      // Replace rather than mutate so Mithril reliably sees the change.
      this.state = { ...this.state, [type]: { ...state, loading: true, error: null } };
    }

    return app
      .request({
        method: 'GET',
        url: `${app.forum.attribute('apiUrl')}/happy-gify/search`,
        params: { q: requestedQuery, page, type },
      })
      .then((response) => {
        if (type !== this.type || requestedQuery !== this.query) return;

        const items = response.data.items || [];
        const previous = this.state[type];

        this.state = {
          ...this.state,
          [type]: {
            items: append ? [...previous.items, ...items] : items,
            hasNext: !!response.data.hasNext,
            page,
            loading: false,
            error: null,
          },
        };

        this.loadingMore = false;

        m.redraw();
      })
      .catch((error) => {
        if (type !== this.type || requestedQuery !== this.query) return;

        const code = error && error.response && error.response.errors && error.response.errors[0] && error.response.errors[0].code;

        const message =
          code === 'missing_api_key'
            ? app.translator.trans('happydigital-gify.forum.no_api_key')
            : app.translator.trans('happydigital-gify.forum.loading_failed');

        this.state = {
          ...this.state,
          // A failed "load more" keeps the results already on screen; only a
          // fresh load replaces the grid with the error state.
          [type]: { ...this.state[type], loading: false, error: append ? null : message },
        };

        this.loadingMore = false;

        m.redraw();
      });
  }

  /**
   * Switching tabs reuses whatever that tab already loaded; it only fetches
   * when the tab is empty or the search term changed while it was hidden.
   */
  switchTab(type) {
    if (type === this.type) return;

    this.type = type;

    const state = this.state[type];

    if (!state.items.length && !state.error) {
      this.load();
    }
  }

  /**
   * Debounced so typing does not fire a request per keystroke.
   */
  onSearchInput(value) {
    this.query = value;

    if (this.searchTimeout) clearTimeout(this.searchTimeout);

    this.searchTimeout = setTimeout(() => {
      // The other tab's cached results are for the old term, so drop them and
      // let it refetch when the user goes back.
      TABS.filter((type) => type !== this.type).forEach((type) => {
        this.state = { ...this.state, [type]: { items: [], page: 1, hasNext: false, loading: false, error: null } };
      });

      this.load();
    }, 350);
  }

  /**
   * Loads the next page once the grid is scrolled near its end.
   */
  onScroll(e) {
    const el = e.target;

    if (el.scrollHeight - el.scrollTop - el.clientHeight > INFINITE_SCROLL_MARGIN) return;

    this.load(true);
  }

  content() {
    return (
      <div className="Modal-body HappyGify-body">
        <div className="HappyGify-header">
          <nav className="HappyGify-tabs">
            {TABS.map((type) => (
              <button
                type="button"
                key={type}
                className={classList('HappyGify-tab', { active: type === this.type })}
                aria-selected={type === this.type}
                onclick={() => this.switchTab(type)}
              >
                {app.translator.trans(`happydigital-gify.forum.tab_${type}`)}
              </button>
            ))}
          </nav>

          <input
            className="FormControl HappyGify-search"
            type="search"
            value={this.query}
            placeholder={app.translator.trans(`happydigital-gify.forum.search_placeholder_${this.type}`)}
            aria-label={app.translator.trans(`happydigital-gify.forum.search_placeholder_${this.type}`)}
            oninput={(e) => this.onSearchInput(e.target.value)}
          />
        </div>

        <div className="HappyGify-results" onscroll={(e) => this.onScroll(e)}>
          {this.results()}
        </div>

        {/* KLIPY attribution is required by their guidelines and must stay
            visible wherever their content is shown. */}
        <div className="HappyGify-attribution">
          <img
            className="HappyGify-attributionLogo"
            src={app.forum.attribute('baseUrl') + '/assets/extensions/happydigital-gify/powered_by_klipy.svg'}
            alt={app.translator.trans('happydigital-gify.forum.powered_by')}
          />
        </div>
      </div>
    );
  }

  results() {
    const state = this.current();

    if (state.loading) {
      return (
        <div className="HappyGify-state">
          <LoadingIndicator />
        </div>
      );
    }

    if (state.error) {
      return (
        <div className="HappyGify-state">
          <p>{state.error}</p>
          <Button className="Button" onclick={() => this.load()}>
            {app.translator.trans('happydigital-gify.forum.retry')}
          </Button>
        </div>
      );
    }

    if (!state.items.length) {
      return <div className="HappyGify-state">{app.translator.trans('happydigital-gify.forum.no_results')}</div>;
    }

    return (
      <div>
        {!this.query ? <div className="HappyGify-sectionLabel">{app.translator.trans('happydigital-gify.forum.trending')}</div> : null}

        {/* Both types keep their own aspect ratio in a column layout; the tab
            only changes how many columns and whether tiles get a background. */}
        <div className={classList('HappyGify-grid', `HappyGify-grid--${this.type}`)}>
          {buildColumns(state.items, this.columnCount()).map((column, index) => (
            <div className="HappyGify-column" key={index}>
              {column.map((item) => this.tile(item))}
            </div>
          ))}
        </div>

        {this.loadingMore ? (
          <div className="HappyGify-more">
            <LoadingIndicator size="small" />
          </div>
        ) : null}
      </div>
    );
  }

  tile(item) {
    // Reserving the tile's space before the image arrives stops the grid
    // reflowing as GIFs stream in. This is the one inline style here: the
    // ratio is per-item data from the API, so it cannot live in the
    // stylesheet. All the actual layout and colour stays in less/forum.less.
    const ratio = item.width > 0 && item.height > 0 ? item.width / item.height : 1;

    return (
      <button
        type="button"
        key={item.id}
        className="HappyGify-tile"
        title={item.title}
        aria-label={item.title || app.translator.trans('happydigital-gify.forum.untitled')}
        style={{ '--happy-gify-ratio': ratio }}
        onclick={() => this.select(item)}
      >
        <img src={item.preview} alt={item.title} loading="lazy" />
      </button>
    );
  }

  /**
   * Inserts the GIF into the composer as Markdown and closes.
   */
  select(item) {
    if (this.attrs.onselect) {
      this.attrs.onselect(item);
    }

    this.hide();
  }

  onsubmit(e) {
    // The grid does the choosing; there is nothing to submit.
    e.preventDefault();
  }
}
